const LOCAL_PACKAGE_MUTATION_EVENT_TTL_MS = 30_000
const MAX_OBSERVED_PACKAGE_BROADCAST_EVENTS = 500
const localPackageMutationEvents = new Map<string, { patientUuid: string; expiresAt: number }>()
const observedPackageBroadcastEvents = new Set<string>()

export const PACKAGE_BROADCAST_EVENT = "encounter-package-broadcast"

export interface PackageBroadcastEventDetail {
  actionType: "encounter_package_purchase_changed" | "encounter_package_usage_changed"
  eventId: string | null
  patientUuid: string
  encounterUuid: string | null
}

const pruneExpiredLocalPackageMutationEvents = (now: number) => {
  localPackageMutationEvents.forEach((entry, eventId) => {
    if (entry.expiresAt <= now) {
      localPackageMutationEvents.delete(eventId)
    }
  })
}

export const createLocalPackageMutationEvent = (patientUuid: string): string => {
  const eventId =
    typeof globalThis.crypto?.randomUUID === "function" ? globalThis.crypto.randomUUID() : `${Date.now()}-${Math.random().toString(16).slice(2)}`
  pruneExpiredLocalPackageMutationEvents(Date.now())
  localPackageMutationEvents.set(eventId, {
    patientUuid,
    expiresAt: Date.now() + LOCAL_PACKAGE_MUTATION_EVENT_TTL_MS,
  })
  return eventId
}

export const unregisterLocalPackageMutationEvent = (eventId: string) => {
  localPackageMutationEvents.delete(eventId)
}

export const isLocalPackageMutationEvent = (eventId: string | null | undefined, patientUuid: string): boolean => {
  if (!eventId) {
    return false
  }

  pruneExpiredLocalPackageMutationEvents(Date.now())
  const event = localPackageMutationEvents.get(eventId)
  return Boolean(event && event.patientUuid === patientUuid)
}

export const shouldReloadCurrentChartPackages = (detail: PackageBroadcastEventDetail, selectedPatientUuid: string | null | undefined): boolean =>
  Boolean(selectedPatientUuid && detail.patientUuid === selectedPatientUuid && !isLocalPackageMutationEvent(detail.eventId, detail.patientUuid))

export const shouldReloadEncounterPackages = (
  detail: PackageBroadcastEventDetail,
  activePatientUuid: string | null | undefined,
  activeEncounterUuid: string | null | undefined
): boolean =>
  Boolean(detail.encounterUuid && activeEncounterUuid && detail.encounterUuid === activeEncounterUuid && detail.patientUuid === activePatientUuid)

export const getPackageBroadcastKey = (detail: PackageBroadcastEventDetail): string =>
  detail.eventId || [detail.actionType, detail.patientUuid, detail.encounterUuid || "package"].join(":")

export const shouldApplyPackageRequestResult = (
  requestKey: string,
  requestSeq: number,
  currentRequestKey: string,
  currentRequestSeq: number
): boolean => requestKey === currentRequestKey && requestSeq === currentRequestSeq
export const claimPackageBroadcastEvent = (broadcastKey: string): boolean => {
  if (observedPackageBroadcastEvents.has(broadcastKey)) {
    return false
  }

  observedPackageBroadcastEvents.add(broadcastKey)
  if (observedPackageBroadcastEvents.size > MAX_OBSERVED_PACKAGE_BROADCAST_EVENTS) {
    const oldestKey = observedPackageBroadcastEvents.values().next().value
    if (oldestKey) {
      observedPackageBroadcastEvents.delete(oldestKey)
    }
  }
  return true
}

export const releasePackageBroadcastEvent = (broadcastKey: string) => {
  observedPackageBroadcastEvents.delete(broadcastKey)
}

export const runPackageMutationAfterSave = async <T>(
  saveBeforeMutation: (() => Promise<boolean>) | undefined,
  mutation: () => Promise<T>
): Promise<T | null> => {
  if (saveBeforeMutation && !(await saveBeforeMutation())) {
    return null
  }

  return mutation()
}

export const claimLocalPackageMutationReload = (
  eventId: string,
  mutationEncounterUuid: string | null | undefined,
  activeEncounterUuid: string | null | undefined
): boolean => Boolean(mutationEncounterUuid && mutationEncounterUuid === activeEncounterUuid && claimPackageBroadcastEvent(eventId))

export const dispatchPackageBroadcastEvent = (detail: PackageBroadcastEventDetail) => {
  window.dispatchEvent(new CustomEvent<PackageBroadcastEventDetail>(PACKAGE_BROADCAST_EVENT, { detail }))
}
